import type { ButtonHTMLAttributes, ReactNode } from "react";
import { AlertTriangle, Coins, Inbox, LoaderCircle, RefreshCw } from "lucide-react";
import { formatBucks, initials } from "../lib/format";

export function SafeText({ children, className }: { children: string; className?: string }) {
  return (
    <bdi dir="auto" className={className}>
      {children}
    </bdi>
  );
}

export function Avatar({
  name,
  src,
  size = "md",
}: {
  name: string;
  src?: string | null;
  size?: "sm" | "md" | "lg" | "xl";
}) {
  return src ? (
    <img className={`avatar avatar--${size}`} src={src} alt={`Profilbild von ${name}`} />
  ) : (
    <span
      className={`avatar avatar--${size} avatar--fallback`}
      role="img"
      aria-label={`Avatar von ${name}`}
    >
      {initials(name)}
    </span>
  );
}

export function Money({ value, compact = false }: { value: unknown; compact?: boolean }) {
  return (
    <span className={`money${compact ? " money--compact" : ""}`}>
      <Coins aria-hidden="true" size={compact ? 14 : 17} />
      {formatBucks(value)}
    </span>
  );
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </header>
  );
}

export function Card({
  children,
  className = "",
  as: Tag = "section",
}: {
  children: ReactNode;
  className?: string;
  as?: "section" | "article" | "div";
}) {
  return <Tag className={`card ${className}`}>{children}</Tag>;
}

const statusLabels: Record<string, string> = {
  ACTIVE: "Aktiv",
  OPEN: "Offen",
  LIVE: "Live",
  RUNNING: "Läuft",
  WAITING: "Wartet",
  PAUSED: "Pausiert",
  COMPLETED: "Beendet",
  FINISHED: "Beendet",
  ACCEPTED: "Angenommen",
  REJECTED: "Abgelehnt",
  DISABLED: "Deaktiviert",
  PENDING: "Ausstehend",
  PENDING_APPROVAL: "Freigabe ausstehend",
  PENDING_DELETION: "Löschung vorgemerkt",
  ONLINE: "Online",
  OFFLINE: "Offline",
  HEALTHY: "In Ordnung",
  DEGRADED: "Beeinträchtigt",
  ERROR: "Fehler",
};

export function StatusBadge({ status, label }: { status: string; label?: string }) {
  const normalized = status.toUpperCase();
  const tone = /ACTIVE|OPEN|LIVE|RUNNING|ACCEPTED|ONLINE|HEALTHY/u.test(normalized)
    ? "positive"
    : /ERROR|REJECTED|FAILED|DISABLED|CRITICAL/u.test(normalized)
      ? "negative"
      : /PENDING|WAIT|PAUSED|DEGRADED/u.test(normalized)
        ? "warning"
        : "neutral";
  return (
    <span className={`status status--${tone}`}>{label ?? statusLabels[normalized] ?? status}</span>
  );
}

export function LoadingState({
  label = "Daten werden geladen …",
  rows = 3,
}: {
  label?: string;
  rows?: number;
}) {
  return (
    <div className="state state--loading" role="status" aria-live="polite">
      <LoaderCircle className="spin" aria-hidden="true" />
      <span>{label}</span>
      <div className="skeleton-list" aria-hidden="true">
        {Array.from({ length: rows }, (_, index) => (
          <span key={index} />
        ))}
      </div>
    </div>
  );
}

export function ErrorState({
  title = "Das hat nicht funktioniert",
  message,
  onRetry,
}: {
  title?: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="state state--error" role="alert">
      <AlertTriangle aria-hidden="true" />
      <div>
        <h2>{title}</h2>
        <p>{message}</p>
      </div>
      {onRetry && (
        <button className="button button--secondary" type="button" onClick={onRetry}>
          <RefreshCw aria-hidden="true" size={16} />
          Erneut versuchen
        </button>
      )}
    </div>
  );
}

export function EmptyState({
  title,
  message,
  action,
}: {
  title: string;
  message: string;
  action?: ReactNode;
}) {
  return (
    <div className="state state--empty">
      <Inbox aria-hidden="true" />
      <h2>{title}</h2>
      <p>{message}</p>
      {action}
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <label className={`field${error ? " field--error" : ""}`}>
      <span className="field__label">{label}</span>
      {children}
      {hint && <small>{hint}</small>}
      {error && <small role="alert">{error}</small>}
    </label>
  );
}

interface GuardedButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  allowed: boolean;
  unavailableReason: string;
  children: ReactNode;
}

export function GuardedButton({
  allowed,
  unavailableReason,
  children,
  className = "button",
  ...props
}: GuardedButtonProps) {
  return (
    <span className="guarded-action">
      <button
        {...props}
        className={className}
        disabled={!allowed || props.disabled}
        title={!allowed ? unavailableReason : props.title}
        aria-describedby={!allowed ? `${props.id ?? "guarded-action"}-reason` : undefined}
      >
        {children}
      </button>
      {!allowed && (
        <small id={`${props.id ?? "guarded-action"}-reason`} className="guarded-action__reason">
          {unavailableReason}
        </small>
      )}
    </span>
  );
}

export function Segmented({
  value,
  onChange,
  options,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  label: string;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={value === option.value ? "is-active" : ""}
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
